import { useMemo } from "react";

import {
  faFlask,
  faPenToSquare,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "motion/react";
import { useLocalStorage } from "usehooks-ts";

import { DashboardEditActions } from "src/analytics/dashboard/DashboardEditActions";
import { DashboardGrid } from "src/analytics/dashboard/DashboardGrid";
import { DashboardLastEditedInfo } from "src/analytics/dashboard/DashboardLastEditedInfo";
import { DashboardMetricsProvider } from "src/analytics/dashboard/DashboardMetricsContext";
import { DashboardRefreshButton } from "src/analytics/dashboard/DashboardRefreshButton";
import { defaultDashboardWindow } from "src/analytics/dashboard/dashboardFilterParams";
import { JustPinnedProvider } from "src/analytics/dashboard/JustPinnedContext";
import { useDashboard } from "src/analytics/dashboard/useDashboard";
import { useDashboardLayoutEditing } from "src/analytics/dashboard/useDashboardLayoutEditing";
import { useFlow } from "src/api/queries";
import { CopilotButton } from "src/base-components/CopilotButton";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { Realm } from "src/clients/analytics-dashboard";
import { CopilotSidebar } from "src/flow/analytics/CopilotSidebar";
import { useSpecialDomainLabels } from "src/flow/analytics/useSpecialDomainLabels";
import { FlowPageLayout } from "src/flow/FlowPageLayout";
import { SubHeader } from "src/flow/SubHeader";
import { useEnvironment } from "src/hooks/useEnvironment";
import { FlowPageParams } from "src/router/urls";
import { type StrictDateRange, useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const FlowAnalyticsContent: React.FC = () => {
  const { flow_id } = useParamsDecode<FlowPageParams>();
  const { data: flow } = useFlow(flow_id, {
    refetchOnMount: true,
  });
  const [environment, setEnvironment] = useEnvironment();
  const labelDictionaries = useSpecialDomainLabels(flow_id, flow?.name ?? "");
  const [isCopilotOpen, setIsCopilotOpen] = useLocalStorage(
    "flowAnalyticsCopilotOpen",
    false,
  );

  const defaultWindow = useMemo<StrictDateRange>(
    () => defaultDashboardWindow(),
    [],
  );
  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow<StrictDateRange>(defaultWindow, "flowAnalyticsTimeWindow");
  const editing = useDashboardLayoutEditing(Realm.FLOWS, flow_id);
  const { dashboard } = useDashboard(Realm.FLOWS, flow_id);
  const pins = Object.values(dashboard.pins);

  return (
    <JustPinnedProvider key={`${Realm.FLOWS}:${flow_id}`}>
      <FlowPageLayout
        flow={flow}
        headerActions={
          <>
            <div className="pr-3">
              <DashboardLastEditedInfo realm={Realm.FLOWS} realmId={flow_id} />
            </div>
            <AnimatePresence initial={false}>
              {!isCopilotOpen && (
                <m.div
                  key="ask-copilot"
                  className="pr-3"
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                >
                  <CopilotButton
                    dataLoc="ask-copilot-button"
                    onClick={() => setIsCopilotOpen(true)}
                  >
                    Ask Copilot
                  </CopilotButton>
                </m.div>
              )}
            </AnimatePresence>
          </>
        }
        rightPanel={
          flow && (
            <AnimatePresence initial={false} mode="wait">
              {isCopilotOpen && (
                <CopilotSidebar
                  key="copilot-sidebar"
                  flowId={flow_id}
                  flowName={flow.name}
                  isLayoutEditing={editing.isEditing}
                  onClose={() => setIsCopilotOpen(false)}
                />
              )}
            </AnimatePresence>
          )
        }
      >
        <DashboardMetricsProvider pins={pins} timeWindow={timeWindow}>
          <SubHeader title="Analytics" paddedParent>
            {editing.isEditing ? (
              <DashboardEditActions
                isSaving={editing.isSaving}
                onCancel={editing.cancel}
                onSave={editing.save}
              />
            ) : (
              <>
                <SubHeader.DatePicker
                  placeholder="Last 30 days"
                  value={dateRangePickerValue}
                  onChange={onDateRangePickerChange}
                />
                <DashboardRefreshButton />
                <span className="h-full w-12 border-gray-200 border-l p-3.5">
                  <EllipsisOptionsDropdown
                    elements={[
                      {
                        key: "Edit layout",
                        icon: faPenToSquare,
                        action: editing.start,
                        disabled: !editing.canEdit,
                      },
                      {
                        key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"}`,
                        icon: environment === "sandbox" ? faWavePulse : faFlask,
                        action: () =>
                          setEnvironment(
                            environment === "sandbox" ? "live" : "sandbox",
                          ),
                      },
                    ]}
                    iconSize="sm"
                    placement="bottom start"
                  />
                </span>
              </>
            )}
          </SubHeader>
          <DashboardGrid
            editLayout={editing.layout}
            isEditing={editing.isEditing}
            isSaving={editing.isSaving}
            labelDictionaries={labelDictionaries}
            onEditLayoutChange={editing.onLayoutChange}
            onOpenCopilot={() => setIsCopilotOpen(true)}
            realm={Realm.FLOWS}
            realmId={flow_id}
          />
        </DashboardMetricsProvider>
      </FlowPageLayout>
    </JustPinnedProvider>
  );
};
